import React, { useState } from "react";
import { Button, Card, Popconfirm, Space, Typography, Upload, message } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Success } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";

const { Text } = Typography;

const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_MB = 5;

// The company logo printed on invoices, offers, work reports and the other documents.
// Uploaded through /files/upload, then saved on the account with PUT /users/me { logo }.
const CompanyLogo = ({ logo, updateParent }) => {
  const { t } = useTranslation("dashboard");
  const [busy, setBusy] = useState(false);

  const save = async value => {
    setBusy(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/users/me`,
        { logo: value },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      Success(value ? t("Logo saved") : t("Logo removed"));
      updateParent();
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setBusy(false);
    }
  };

  const beforeUpload = file => {
    if (!ACCEPT.split(",").includes(file.type)) {
      message.error(t("Please choose a PNG, JPG, WebP or SVG image."));
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      message.error(t("The image may be at most {{mb}} MB.", { mb: MAX_MB }));
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const onChange = ({ file }) => {
    if (file.status === "uploading") setBusy(true);
    else if (file.status === "done") save(file.response);
    else if (file.status === "error") {
      setBusy(false);
      message.error(t("{{name}} file upload failed.", { name: file.name }));
    }
  };

  return (
    <Card title={t("Company logo")} className="mb-3">
      <div className="d-flex flex-wrap align-items-center gap-3">
        <div
          style={{
            width: 220,
            height: 90,
            border: "1px dashed var(--ant-color-border, #d9d9d9)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            padding: 8,
            flexShrink: 0
          }}
        >
          {logo?.url ? (
            <img
              src={logo.url}
              alt={t("Company logo")}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <Text type="secondary">{t("No logo yet")}</Text>
          )}
        </div>
        <div style={{ minWidth: 0, flex: "1 1 240px" }}>
          <Text type="secondary" className="d-block mb-2">
            {t(
              "Shown on your invoices, offers, work reports and other documents. Best: PNG or SVG with a transparent background, at least 400 px wide."
            )}
          </Text>
          <Space wrap>
            <Upload
              name="file"
              action={`${process.env.REACT_APP_BACKEND_URL}/files/upload`}
              headers={{ Authorization: `Bearer ${localStorage.getItem("token")}` }}
              accept={ACCEPT}
              maxCount={1}
              showUploadList={false}
              beforeUpload={beforeUpload}
              onChange={onChange}
              disabled={busy}
            >
              <Button type="primary" icon={<UploadOutlined />} loading={busy}>
                {logo?.url ? t("Replace logo") : t("Upload logo")}
              </Button>
            </Upload>
            {logo?.url && (
              <Popconfirm
                title={t("Remove the logo from your documents?")}
                okText={t("Remove")}
                cancelText={t("Cancel")}
                onConfirm={() => save(null)}
              >
                <Button danger icon={<DeleteOutlined />} disabled={busy}>
                  {t("Remove")}
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>
    </Card>
  );
};

export default CompanyLogo;
